"use client";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import NoNavPage from "@/components/NoNavPage";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
// import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
// import { Checkbox } from "@/components/ui/checkbox";
import { Textarea } from "@/components/ui/textarea";
import { useState } from "react";
import * as Sentry from "@sentry/react";

// ✅ Validation schema with a relaxed tone
const betaRegistrationSchema = z.object({
  fullName: z.string().min(1, "What's your name?"),
  orgName: z.string().optional(), // Optional for homelab users
  email: z.string().email("Gotta be a valid email."),
  phone: z.string().optional(),
  linuxDevices: z.number().min(1, "How many Linux machines do you have?"),
  debianDevices: z.number().min(0, "How many of those run Debian or Ubuntu?"),
  whyAptFleet: z.string().optional(),
});

// Add this type definition after the schema
type BetaRegistrationData = z.infer<typeof betaRegistrationSchema>;

const RegisterBeta = () => {
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<BetaRegistrationData>({
    resolver: zodResolver(betaRegistrationSchema),
  });

  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");

  const onSubmit = async (data: BetaRegistrationData) => {
    console.log("Registration data:", data);
    setSuccessMessage("");
    setErrorMessage("");

    try {
      const response = await fetch(
        "https://trigger.comnoco.com/t/688b896c-5b7a-4170-8d0b-45b6210e3376/Beta-Signup",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(data),
        }
      );

      if (!response.ok) {
        throw new Error("Something went wrong. Try again?");
      }

      setSuccessMessage("You're in! We'll be in touch soon.");
    } catch (error) {
      Sentry.captureException(error);
      console.error("Registration error:", error);
      setErrorMessage("Oops! Something went wrong. Try again?");
    }
  };
  return (
    <NoNavPage title="">
      <div className="max-w-3xl mx-auto py-8">
        <Card>
          <CardContent className="p-6">
            <h2 className="text-2xl font-bold mb-4">Join the AptFleet Beta</h2>
            <p className="text-muted-foreground mb-6">
              Whether you're managing <strong>servers at work</strong> or{" "}
              <strong>homelab setups in your garage</strong>, AptFleet makes
              package updates easy. Sign up and get early access before we
              launch!
            </p>

            {errorMessage && (
              <div className="p-4 bg-red-100 text-red-700 rounded-md">
                {errorMessage}
              </div>
            )}

            {successMessage ? (
              <div className="p-4 bg-green-100 text-green-700 rounded-md">
                {successMessage}
              </div>
            ) : (
              <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
                <Label>Your Name</Label>
                <Input {...register("fullName")} />
                {errors.fullName && (
                  <p className="text-red-500 text-sm">
                    {errors.fullName.message}
                  </p>
                )}

                <Label>Organization Name (Optional)</Label>
                <Input
                  {...register("orgName")}
                  placeholder="Leave blank if it's just you"
                />

                <Label>Email Address</Label>
                <Input type="email" {...register("email")} />
                {errors.email && (
                  <p className="text-red-500 text-sm">{errors.email.message}</p>
                )}

                <Label>Phone Number (Optional)</Label>
                <Input
                  type="tel"
                  {...register("phone")}
                  placeholder="Only if you want us to call"
                />

                <Label>How many Linux devices do you manage?</Label>
                <Input
                  type="number"
                  {...register("linuxDevices", { valueAsNumber: true })}
                />

                <Label>How many of those run Debian or Ubuntu?</Label>
                <Input
                  type="number"
                  {...register("debianDevices", { valueAsNumber: true })}
                />

                <Label>Why AptFleet? (Optional)</Label>
                <Textarea
                  {...register("whyAptFleet")}
                  placeholder="What problem are you trying to solve?"
                />

                <Button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full"
                >
                  {isSubmitting ? "Submitting..." : "Sign Up"}
                </Button>
              </form>
            )}
          </CardContent>
        </Card>
      </div>
    </NoNavPage>
  );
};

export default RegisterBeta;
